// What Enter does in a chat composer. Shared rule with Wavebox Connect's
// composer: Enter sends, Shift/Alt+Enter and Enter inside an open ``` fence
// insert a newline, Ctrl/Cmd+Enter always sends, and an IME that is
// composing (Japanese, Chinese, Korean) keeps Enter for its conversion.

// A fence line: up to three spaces, then three or more backticks or tildes,
// then the rest of the line (an info string on an opening fence)
const FENCE_LINE_RE = /^ {0,3}(`{3,}|~{3,})(.*)$/

/**
 * Whether the caret is inside a fenced code block, given the text before it.
 * Follows CommonMark: a fence closes only on a line of the same character, at
 * least as long as the opening one, with nothing after it but spaces; and a
 * backtick fence whose info string contains a backtick is not a fence at all.
 * Pass the text up to the caret, so editing inside a block that is closed
 * further down still counts as inside it.
 * @param {string} textBeforeCaret - the composer's text up to the caret
 * @returns {boolean} true when the caret sits inside an open code block
 */
export function hasUnclosedCodeFence (textBeforeCaret) {
  let open = null
  for (const line of (textBeforeCaret || '').split('\n')) {
    const match = FENCE_LINE_RE.exec(line)
    if (!match) { continue }
    const [, marker, rest] = match
    const char = marker[0]
    if (!open) {
      if (char === '`' && rest.includes('`')) { continue }
      open = { char, length: marker.length }
    } else if (char === open.char && marker.length >= open.length && rest.trim() === '') {
      open = null
    }
  }
  return open !== null
}

/**
 * Decides what an Enter keydown should do.
 * @param {{isComposing?: boolean, shiftKey?: boolean, altKey?: boolean, ctrlKey?: boolean, metaKey?: boolean}} key - the keydown's modifier state
 * @param {string} textBeforeCaret - the composer's text up to the caret
 * @returns {'send' | 'newline' | 'insertNewline' | 'ignore'} 'newline' leaves the browser to add the line,
 *   'insertNewline' means the caller must add it (Alt+Enter adds nothing by default),
 *   'ignore' leaves the event alone (the IME owns it)
 */
export function enterAction (key, textBeforeCaret) {
  if (key.isComposing) { return 'ignore' }
  if (key.ctrlKey || key.metaKey) { return 'send' }
  if (key.shiftKey) { return 'newline' }
  if (key.altKey) { return 'insertNewline' }
  if (hasUnclosedCodeFence(textBeforeCaret)) { return 'newline' }
  return 'send'
}

/**
 * Whether a keydown is the always-send shortcut, which autocomplete menus must
 * let through rather than treat as "pick this item"
 * @param {{key: string, ctrlKey?: boolean, metaKey?: boolean}} e - the keydown
 * @returns {boolean} true for Ctrl/Cmd+Enter
 */
export function isSendShortcut (e) {
  return e.key === 'Enter' && !!(e.ctrlKey || e.metaKey)
}
